/**
 * CsaiTest — channel-detail "Prueba de anuncios" tab.
 *
 * A VALIDATION surface (not a production playback path) for client-side ad
 * insertion (CSAI): it plays a channel's live HLS through the app's existing
 * Vidstack + self-hosted hls.js stack, watches the manifest for the SCTE-35 /
 * `#EXT-X-CUE-OUT` markers the Circa edge emits, and fires VAST ads through the
 * Google IMA HTML5 SDK against the four local test creatives in
 * `public/ads/ad-{1..4}.mp4`.
 *
 * Two ways to validate:
 *   1. "Disparar anuncio" — manual trigger; isolates IMA + creatives + the
 *      pause/overlay/resume flow from the stream.
 *   2. "Detección automática" — polls the live manifest; a fresh CUE-OUT /
 *      SCTE-35 in the playlist requests an ad break automatically.
 *
 * The debug panel logs every detected cue and every IMA ad event so you can see
 * insertion working end to end. IMA SDK is loaded from its CDN at click time —
 * fine on dev (no CSP); production would need the imasdk.googleapis.com /
 * doubleclick hosts allow-listed (see packages/vidstack-ima-dai/README.md).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Btn, Field, Input, Select, Toggle, Pill, Panel } from '../../components/primitives';
import { AssetPlayer } from '../../components/asset-player';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { parseManifestCues, firstVariantUrl, cueKey, type Cue } from './csai/adMarkers';
import { buildVast, type VastMode } from './csai/vast';

declare global {
  interface Window {
    google?: any;
  }
}

const IMA_SDK_SRC = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
const POLL_MS = 3000;
const LOG_CAP = 120;

type SdkState = 'idle' | 'loading' | 'ready' | 'error';
type LogTone = 'info' | 'cue' | 'ad' | 'error';
interface LogEntry { id: number; t: number; tone: LogTone; msg: string }

/** Inject the IMA SDK once; resolve when `window.google.ima` is available. */
function loadImaSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.ima) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${IMA_SDK_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('IMA SDK failed to load')));
      return;
    }
    const s = document.createElement('script');
    s.src = IMA_SDK_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('IMA SDK failed to load'));
    document.head.appendChild(s);
  });
}

export function CsaiTest({ c }: { c: { _id: string; name?: string } }) {
  const live = useQuery(api.channels.getChannelWithState, { channelId: c._id as any }) as
    | { livePlaybackUrl?: string }
    | null
    | undefined;
  const channelUrl = live?.livePlaybackUrl;

  const [urlInput, setUrlInput] = useState('');
  const [vastMode, setVastMode] = useState<VastMode>('pod');
  const [autoDetect, setAutoDetect] = useState(false);
  const [sdkState, setSdkState] = useState<SdkState>('idle');
  const [adState, setAdState] = useState<'none' | 'requesting' | 'playing'>('none');
  const [log, setLog] = useState<LogEntry[]>([]);
  const [cueLines, setCueLines] = useState<Cue[]>([]);
  const [pollErr, setPollErr] = useState<string | null>(null);

  // The URL the player + poller actually use. Defaults to the channel's live URL
  // when it resolves, but the operator can override it in the input.
  const effectiveUrl = urlInput.trim() || channelUrl || '';

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const adContainerRef = useRef<HTMLDivElement | null>(null);
  const displayContainerRef = useRef<any>(null);
  const adsLoaderRef = useRef<any>(null);
  const adsManagerRef = useRef<any>(null);
  const rotationRef = useRef(0);
  const seenCuesRef = useRef<Set<string>>(new Set());
  const primedRef = useRef(false);
  const logIdRef = useRef(0);

  const addLog = useCallback((tone: LogTone, msg: string) => {
    setLog((prev) => {
      const next = [{ id: logIdRef.current++, t: Date.now(), tone, msg }, ...prev];
      return next.slice(0, LOG_CAP);
    });
  }, []);

  const contentVideo = useCallback(
    () => wrapperRef.current?.querySelector('video') as HTMLVideoElement | null,
    [],
  );

  const slotSize = useCallback(() => {
    const v = contentVideo();
    return { w: v?.clientWidth || 640, h: v?.clientHeight || 360 };
  }, [contentVideo]);

  // ---- IMA ad lifecycle -------------------------------------------------

  const showAdOverlay = (on: boolean) => {
    if (adContainerRef.current) {
      adContainerRef.current.style.pointerEvents = on ? 'auto' : 'none';
      adContainerRef.current.style.zIndex = on ? '20' : '0';
    }
  };

  const onAdError = useCallback(
    (e: any) => {
      const err = e?.getError?.();
      addLog('error', `AD_ERROR: ${err?.getMessage?.() ?? 'unknown'}`);
      try {
        adsManagerRef.current?.destroy();
      } catch { /* noop */ }
      adsManagerRef.current = null;
      contentVideo()?.play().catch(() => {});
      showAdOverlay(false);
      setAdState('none');
    },
    [addLog, contentVideo],
  );

  const onAdsManagerLoaded = useCallback(
    (e: any) => {
      const ima = window.google.ima;
      const video = contentVideo();
      const settings = new ima.AdsRenderingSettings();
      settings.restoreCustomPlaybackStateOnAdBreakComplete = true;
      const mgr = e.getAdsManager(video, settings);
      adsManagerRef.current = mgr;

      const E = ima.AdEvent.Type;
      mgr.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, onAdError);
      mgr.addEventListener(E.CONTENT_PAUSE_REQUESTED, () => {
        addLog('ad', 'CONTENT_PAUSE_REQUESTED → pausing content');
        video?.pause();
        showAdOverlay(true);
        setAdState('playing');
      });
      mgr.addEventListener(E.CONTENT_RESUME_REQUESTED, () => {
        addLog('ad', 'CONTENT_RESUME_REQUESTED → resuming content');
        showAdOverlay(false);
        video?.play().catch(() => {});
        setAdState('none');
      });
      mgr.addEventListener(E.LOADED, () => addLog('ad', 'ad LOADED'));
      mgr.addEventListener(E.STARTED, (ev: any) => {
        const ad = ev.getAd?.();
        addLog('ad', `ad STARTED: ${ad?.getAdId?.() ?? '?'} (${Math.round(ad?.getDuration?.() ?? 0)}s)`);
      });
      mgr.addEventListener(E.COMPLETE, () => addLog('ad', 'ad COMPLETE'));
      mgr.addEventListener(E.SKIPPED, () => addLog('ad', 'ad SKIPPED'));
      mgr.addEventListener(E.ALL_ADS_COMPLETED, () => addLog('ad', 'ALL_ADS_COMPLETED'));

      try {
        const { w, h } = slotSize();
        mgr.init(w, h, ima.ViewMode.NORMAL);
        mgr.start();
      } catch (err) {
        addLog('error', `adsManager.start failed: ${String(err)}`);
        onAdError({ getError: () => ({ getMessage: () => String(err) }) });
      }
    },
    [addLog, contentVideo, onAdError, slotSize],
  );

  /** One-time setup: build the AdDisplayContainer (needs a user gesture) +
   *  AdsLoader. Must be triggered by a click. */
  const initAds = useCallback(async () => {
    try {
      setSdkState('loading');
      await loadImaSdk();
      const ima = window.google.ima;
      const video = contentVideo();
      if (!adContainerRef.current || !video) {
        throw new Error('player not mounted yet — start the stream first');
      }
      const display = new ima.AdDisplayContainer(adContainerRef.current, video);
      display.initialize(); // satisfies the IMA user-gesture requirement
      displayContainerRef.current = display;

      const loader = new ima.AdsLoader(display);
      loader.addEventListener(
        ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        onAdsManagerLoaded,
        false,
      );
      loader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, onAdError, false);
      adsLoaderRef.current = loader;

      setSdkState('ready');
      addLog('info', 'IMA SDK ready — display container initialized');
    } catch (err) {
      setSdkState('error');
      addLog('error', `IMA init failed: ${String(err)}`);
    }
  }, [addLog, contentVideo, onAdError, onAdsManagerLoaded]);

  const requestAds = useCallback(
    (reason: string) => {
      const loader = adsLoaderRef.current;
      if (!loader) {
        addLog('error', 'requestAds ignored — IMA not initialized (press "Iniciar prueba")');
        return;
      }
      // A fresh manager per break: discard any prior one.
      try {
        adsManagerRef.current?.destroy();
      } catch { /* noop */ }
      adsManagerRef.current = null;

      const ima = window.google.ima;
      const { w, h } = slotSize();
      const req = new ima.AdsRequest();
      req.adsResponse = buildVast(vastMode, window.location.origin, rotationRef.current);
      rotationRef.current += 1;
      req.linearAdSlotWidth = w;
      req.linearAdSlotHeight = h;
      req.nonLinearAdSlotWidth = w;
      req.nonLinearAdSlotHeight = Math.round(h / 3);
      setAdState('requesting');
      addLog('ad', `requestAds (${vastMode}) — ${reason}`);
      loader.requestAds(req);
    },
    [addLog, slotSize, vastMode],
  );

  // ---- Manifest polling (cue detection) ---------------------------------

  useEffect(() => {
    if (!autoDetect || !effectiveUrl) return;
    let cancelled = false;
    primedRef.current = false;
    seenCuesRef.current = new Set();

    const poll = async () => {
      try {
        const res = await fetch(effectiveUrl, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        let text = await res.text();
        let mediaUrl = effectiveUrl;
        if (/#EXT-X-STREAM-INF/.test(text)) {
          const variant = firstVariantUrl(text, effectiveUrl);
          if (!variant) throw new Error('no variant in master playlist');
          mediaUrl = variant;
          const vres = await fetch(variant, { cache: 'no-store' });
          if (!vres.ok) throw new Error(`variant HTTP ${vres.status}`);
          text = await vres.text();
        }
        if (cancelled) return;
        setPollErr(null);
        const { cues } = parseManifestCues(text);
        setCueLines(cues);

        for (const cue of cues) {
          const key = cueKey(cue);
          if (seenCuesRef.current.has(key)) continue;
          seenCuesRef.current.add(key);
          if (!primedRef.current) continue; // seed existing window without firing
          addLog('cue', `manifest cue: ${cue.kind}${cue.duration ? ` (${cue.duration}s)` : ''} @seg ${cue.segmentIndex}`);
          if (cue.kind === 'CUE-OUT' || cue.kind === 'SCTE35') {
            requestAds(`auto: ${cue.kind} @${mediaUrl.split('/').pop()}`);
          }
        }
        primedRef.current = true;
      } catch (err) {
        if (!cancelled) setPollErr(String(err));
      }
    };

    void poll();
    const iv = window.setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
    };
  }, [autoDetect, effectiveUrl, addLog, requestAds]);

  // Keep IMA ad slot sized to the player.
  useEffect(() => {
    const onResize = () => {
      const mgr = adsManagerRef.current;
      if (mgr && window.google?.ima) {
        const { w, h } = slotSize();
        try {
          mgr.resize(w, h, window.google.ima.ViewMode.NORMAL);
        } catch { /* noop */ }
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [slotSize]);

  // Teardown on unmount.
  useEffect(() => {
    return () => {
      try {
        adsManagerRef.current?.destroy();
      } catch { /* noop */ }
      try {
        adsLoaderRef.current?.destroy();
      } catch { /* noop */ }
    };
  }, []);

  const toneColor: Record<LogTone, string> = {
    info: 'var(--muted-slate)',
    cue: 'var(--focus-purple)',
    ad: 'var(--deep-green)',
    error: 'var(--bad)',
  };

  return (
    <div className="circa-cols-8-4">
      <Panel title="Reproductor de prueba (CSAI)" pad={0} reviewed="wired">
        <div style={{ padding: 24 }}>
          <Field label="URL del manifiesto HLS (.m3u8)" hint={channelUrl ? 'Predeterminado: URL en vivo del canal. Sobrescribe para probar otra fuente.' : 'El canal no está al aire — pega una URL .m3u8 para probar.'}>
            <Input
              value={urlInput}
              onChange={setUrlInput}
              placeholder={channelUrl || 'https://…/index.m3u8'}
              icon="link"
              reviewed="ok"
            />
          </Field>

          {effectiveUrl ? (
            <div ref={wrapperRef} style={{ position: 'relative', marginTop: 16 }}>
              <AssetPlayer
                key={effectiveUrl}
                descriptor={{
                  assetId: c._id,
                  mediaKind: 'video',
                  source: { url: effectiveUrl, contentType: 'application/vnd.apple.mpegurl' },
                  title: c.name,
                  channelId: c._id,
                  durationMs: 0,
                }}
              />
              {/* IMA ad container — overlays the player; click-through until an ad plays. */}
              <div
                ref={adContainerRef}
                style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}
              />
            </div>
          ) : (
            <div style={{ marginTop: 16, padding: 48, textAlign: 'center', color: 'var(--muted-slate)', background: 'var(--lightest-gray)', borderRadius: 12 }}>
              Ingresa una URL .m3u8 o pon el canal al aire para empezar.
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 18 }}>
            <Btn variant="solid" icon="play" reviewed="wired" loading={sdkState === 'loading'} disabled={!effectiveUrl || sdkState === 'ready'} onClick={() => { void initAds(); }}>
              {sdkState === 'ready' ? 'IMA listo' : 'Iniciar prueba'}
            </Btn>
            <Btn variant="ghost" icon="zap" reviewed="wired" disabled={sdkState !== 'ready'} onClick={() => requestAds('manual')}>
              Disparar anuncio
            </Btn>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>VAST</span>
            <Select
              value={vastMode}
              reviewed="ok"
              onChange={(v: string) => setVastMode(v as VastMode)}
              options={[{ value: 'single', label: 'Un anuncio (rota 1-4)' }, { value: 'pod', label: 'Pod (4 anuncios)' }]}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
            <Toggle on={autoDetect} onChange={setAutoDetect} disabled={!effectiveUrl} reviewed="ok" />
            <div>
              <div style={{ fontSize: 14 }}>Detección automática de marcadores</div>
              <div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>
                Sondea el manifiesto cada {POLL_MS / 1000}s; un CUE-OUT/SCTE-35 nuevo dispara un anuncio.
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <Pill tone={adState === 'playing' ? 'good' : adState === 'requesting' ? 'warn' : 'neutral'} reviewed="wired">
              {adState === 'playing' ? 'ANUNCIO EN CURSO' : adState === 'requesting' ? 'SOLICITANDO' : 'CONTENIDO'}
            </Pill>
          </div>

          {pollErr && (
            <p role="alert" style={{ marginTop: 12, fontSize: 13, color: 'var(--bad)' }}>
              Error al sondear el manifiesto: {pollErr} (¿CORS? el origen debe permitir este dominio)
            </p>
          )}
          {sdkState === 'error' && (
            <p role="alert" style={{ marginTop: 12, fontSize: 13, color: 'var(--bad)' }}>
              No se pudo cargar el SDK de IMA. En producción el CSP debe permitir imasdk.googleapis.com.
            </p>
          )}
        </div>
      </Panel>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Panel title="Marcadores en el manifiesto" reviewed="wired">
          {cueLines.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--muted-slate)', margin: 0 }}>
              {autoDetect ? 'Sin marcadores en la ventana actual.' : 'Activa la detección automática para inspeccionar el manifiesto.'}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflow: 'auto' }}>
              {cueLines.map((cue, i) => (
                <div key={`${cueKey(cue)}-${i}`} className="t-mono" style={{ fontSize: 11, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Pill size="sm" tone={cue.kind === 'CUE-IN' ? 'neutral' : 'warn'}>{cue.kind}</Pill>
                  <span style={{ color: 'var(--muted-slate)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cue.raw}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Registro de eventos" reviewed="wired" right={<Btn size="sm" variant="text" reviewed="ok" onClick={() => setLog([])}>Limpiar</Btn>}>
          {log.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--muted-slate)', margin: 0 }}>Sin eventos todavía.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 360, overflow: 'auto' }}>
              {log.map((e) => (
                <div key={e.id} className="t-mono" style={{ fontSize: 11, display: 'flex', gap: 8 }}>
                  <span style={{ color: 'var(--muted-slate)', flexShrink: 0 }}>
                    {new Date(e.t).toLocaleTimeString('es-CL', { hour12: false })}
                  </span>
                  <span style={{ color: toneColor[e.tone] }}>{e.msg}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
